import React, { useEffect, useRef } from "react";
import { drawHeadStyle, type HeadStyleType } from "../../../game-canvas/head-styles";
import { drawEggCluster } from "../../../shared/golden-eggs";
import type { CrownReward } from "../../../shared/royale";
import s from "./RewardArt.module.scss";

// What a Royale reward looks like, moving: a won head big (the head itself
// with its own shine, a stretch of worm behind it swaying), or its eggs as the
// Store's egg cluster. The win screen, the Store's claim and the design lab
// (/debug/royale-end) show it.

export function HeadShowcase({ type }: { type: HeadStyleType }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    let on = true;
    const t0 = performance.now();
    const loop = (now: number) => {
      if (!on) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = Math.round(canvas.clientWidth * dpr), H = Math.round(canvas.clientHeight * dpr);
      if (W && H && (canvas.width !== W || canvas.height !== H)) { canvas.width = W; canvas.height = H; }
      const t = (now - t0) / 1000;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const r = Math.min(canvas.width, canvas.height) * 0.24, hx = canvas.width * 0.56, hy = canvas.height * 0.46;
      // the worm: body radius 4 to the head's 9 (head-styles.ts), trailing down-left
      ctx.strokeStyle = "#ffcf4d"; ctx.lineCap = "round"; ctx.lineWidth = r * 0.9;
      ctx.beginPath();
      for (let i = 0; i <= 24; i++) {
        const u = i / 24;
        const x = hx - u * canvas.width * 0.62, y = hy + u * canvas.height * 0.42 + Math.sin(u * 5 - t * 3) * r * 0.35 * u;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
      drawHeadStyle(ctx, hx, hy, r, -0.55 + Math.sin(t * 1.6) * 0.06, t, type, "#ffcf4d");
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
    return () => { on = false; };
  }, [type]);
  return <canvas ref={ref} className={s.fill} />;
}

export function EggCluster({ count }: { count: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const ctx = ref.current?.getContext("2d");
    if (!ctx) return;
    let on = true;
    const t0 = performance.now();
    const loop = (now: number) => { if (!on) return; drawEggCluster(ctx, 120, (now - t0) / 1000, count, -1); requestAnimationFrame(loop); };
    requestAnimationFrame(loop);
    return () => { on = false; };
  }, [count]);
  return <canvas ref={ref} width={120} height={120} className={s.fill} />;
}

// The reward's art in its frame (the frame's size is the caller's class).
export default function RewardArt({ r, className }: { r: CrownReward; className?: string }) {
  return (
    <span className={`${s.art} ${className || ""}`}>
      {r.item && r.item.kind === "head"
        ? <HeadShowcase type={r.item.id as HeadStyleType} />
        : <EggCluster count={Math.min(4, Math.max(1, Math.round(r.eggs / 10)))} />}
    </span>
  );
}

// The name a reward goes by: the item it brings, else its own.
const ITEM_NAME: Record<string, string> = { crown: "Royale Laurel" };
export const rewardTitle = (r: CrownReward) => (r.item ? ITEM_NAME[r.item.id] || r.name : r.name);
